<template>
  <form class="tml-new-form" @submit="$emit('submit', $event)">
    <slot :errors="errors" :form-data="formData" />
  </form>
</template>

<script>
import {forms, validator} from '@teemill/common/plugins';
import {isEmpty} from 'lodash';

export default {
  name: 'TmlNewForm',

  provide() {
    return {
      /**
       * Provide the register method to all form fields in this form so that
       * the $forms module can reference them in data retreival and validation
       */
      registerNewField: this.registerNewField,

      unregisterField: this.unregisterField,
    };
  },

  /**
   * Name of the form.
   *
   * This is used by the $forms module in data retrieval and validation
   */
  props: {
    name: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fields: {},
      errors: {},
    };
  },

  computed: {
    formData() {
      return this.getValues();
    },
  },

  created() {
    forms.reply(`form-validate-${this.name}`, name => this.validate(name));
    forms.reply(`form-values-${this.name}`, name => this.getValues(name));
  },

  methods: {
    /**
     * Loop over each field registered in the form and return their values as
     * and array
     *
     * ! Internal method
     * ! *
     * ! should be called using $forms.values([FORM_NAME]).then...
     *
     * @return { Array }
     */
    getValues() {
      const values = [];

      Object.keys(this.fields).forEach(field => {
        values[field] = this.getValue(this.fields[field]);
      });

      return values;
    },

    /**
     * Call the getValue method on a form field component to retreive the value
     *
     * ! Internal method
     * ! *
     * ! should be called using $forms.value([FIELD_NAME]).then...
     *
     * @param { Component } field The form field component
     *
     * @return { * }
     */
    getValue(field) {
      return field.getValue();
    },

    /**
     * Validates all registered form fields wrapped in a `tml-new-form` component
     *
     * ! Internal method
     * ! *
     * ! should be called using $forms.validate([FORM_NAME], 'form').then...
     *
     * @returns { Promise } Resolves an object of errors if there are any
     *                      validation exceptions
     */
    validate() {
      return new Promise((resolve, reject) => {
        const validatedFields = Object.keys(this.fields)
          /**
           * Asynchronously loop through each form field and run its validation
           * methods.
           *
           * async in case in future any validation requires promise based execution
           */
          .map(async field => {
            const errors = validator.validate(
              this.getValue(this.fields[field]),
              this.fields[field].$props.validationRules
            );

            return {
              name: field,
              errors,
            };
          });

        /**
         * Wait until all validation methods have resolved before continuing
         */
        Promise.all(validatedFields).then(async result => {
          this.setValidationErrors(result).then(() => {
            const rejectableErrors = {};

            /**
             * Apply all of the form field errors to this component instance.
             *
             * These are available on the default slot as an API
             *
             */
            result.forEach(({name, errors}) => {
              this.errors[name] = errors;

              if (!isEmpty(errors)) {
                rejectableErrors[name] = errors;
              }
            });

            /**
             * Return the errors to the event bus.
             *
             * This will resolve the promise that first called the
             * $forms.validate methods, with a value of the validated errors
             */
            if (!isEmpty(rejectableErrors)) {
              reject(rejectableErrors);
            } else {
              resolve(true);
            }
          });
        });
      });
    },

    /**
     * Loop over each field in the form and set any validation errors
     * on the component instance
     *
     * @param { Array } validatedFields
     */
    async setValidationErrors(validatedFields) {
      validatedFields.forEach(async ({name, errors}) => {
        this.fields[name].setValidationErrors(errors);
      });
    },

    registerNewField(name, component) {
      this.fields[name] = component;
    },

    unregisterField(name) {
      // Todo: find out why this is actually needed
      // this.$delete(this.fields, name);
    },
  },
};
</script>
